/**
 * Visual Blocks Editor
 *
 * Copyright 2014 Massachusetts Institute of Technology
 * http://zerorobotics.org/
 *
 * Licensed under the Apache License, Version 2.0
 *
 * @fileoverview Blocks for basic ZR C++ API.
 * @author Mizan Chowdhury
 */

import * as Blockly from "blockly";
import { zrCppGenerator } from "./generator/spheres/zrCppGenerator";


// ============================================================
// Legacy Zero Robotics compatibility
// ============================================================

function requireInFunction(event) {
    /*
     * Old Zero Robotics Blockly used:
     *
     *     Blockly.Blocks.requireInFunction
     *
     * Modern Blockly may not provide this helper.
     *
     * Blockly calls onchange with:
     *
     *     this   = block
     *     event  = Blockly event
     */

    const block = this;

    if (
        Blockly.Blocks &&
        typeof Blockly.Blocks.requireInFunction === "function"
    ) {
        try {
            Blockly.Blocks.requireInFunction.call(
                block,
                event
            );
        } catch (error) {
            console.warn(
                "Legacy requireInFunction failed:",
                error
            );
        }
    }
}


// ============================================================
// Constants
// ============================================================

const ITEM_OPTIONS = [
    ["Item 0", "0"],
    ["Item 1", "1"],
    ["Item 2", "2"],
    ["Item 3", "3"],
    ["Item 4", "4"],
    ["Item 5", "5"]
];

const PLAYER_OPTIONS = [
    ["my", "0"],
    ["other", "1"]
];

const CHECK_HAVE_PLAYER_OPTIONS = [
    ["me", "1"],
    ["other", "2"],
    ["no-one", "0"]
];


// ============================================================
// SPS / ZONE
// ============================================================


// ------------------------------------------------------------
// zrms17_dropSPSBool
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dropSPSBool"] = {

    init: function () {

        this.appendDummyInput()
            .setAlign(Blockly.ALIGN_CENTRE)
            .appendField("get Num Geysers");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(290);

        this.setTooltip(
            "Get the number of SPS geysers."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_getNumSPSHeld
// ------------------------------------------------------------

Blockly.Blocks["zrms17_getNumSPSHeld"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("get ")
            .appendField(
                new Blockly.FieldDropdown(
                    PLAYER_OPTIONS
                ),
                "playerID"
            )
            .appendField("score");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(290);

        this.setTooltip(
            "Get the selected player's score."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_getZone
// ------------------------------------------------------------

Blockly.Blocks["zrms17_getZone"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("get zone");

        this.appendValueInput("zoneInfo")
            .appendField("zoneInfo")
            .setCheck("array");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(20);

        this.setTooltip(
            "Get zone information."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ============================================================
// DOCK / DROP
// ============================================================


// ------------------------------------------------------------
// zrms17_dockItem
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dockItem"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("dock with item");

        this.setInputsInline(true);

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setColour(20);

        this.setTooltip(
            "Dock with the current item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_dockItemID
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dockItemID"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("dock with")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            );

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(20);

        this.setTooltip(
            "Get the dock item ID."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_dockItemIDVoid
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dockItemIDVoid"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("dock with")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            );

        this.setInputsInline(true);

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setColour(20);

        this.setTooltip(
            "Dock with the selected item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_dropSPSVoid
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dropSPSVoid"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("drop SPS");

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setColour(20);

        this.setTooltip(
            "Drop the SPS."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_dropItem
// ------------------------------------------------------------

Blockly.Blocks["zrms17_dropItem"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("drop current item");

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setColour(20);

        this.setTooltip(
            "Drop the current item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ============================================================
// ITEM INFO
// ============================================================


// ------------------------------------------------------------
// zrms17_getItemLoc
// ------------------------------------------------------------

Blockly.Blocks["zrms17_getItemLoc"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("get location of")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            )
            .appendField("into");

        this.appendValueInput("pos")
            .appendField("pos")
            .setCheck("array");

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setInputsInline(true);

        this.setColour(20);

        this.setTooltip(
            "Get the location of the selected item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_itemInZone
// ------------------------------------------------------------

Blockly.Blocks["zrms17_itemInZone"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("is item")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            )
            .appendField("in zone");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "boolean"
        );

        this.setColour(20);

        this.setTooltip(
            "Check whether the item is in the zone."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_hasItem
// ------------------------------------------------------------

Blockly.Blocks["zrms17_hasItem"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("has item")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            );

        this.setInputsInline(true);

        this.setOutput(
            true,
            "boolean"
        );

        this.setColour(20);

        this.setTooltip(
            "Check whether the player has the selected item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_checkHaveItem
// ------------------------------------------------------------

Blockly.Blocks["zrms17_checkHaveItem"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("check have")
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS.map(
                        ([label, value]) => [
                            label.toLowerCase(),
                            value
                        ]
                    )
                ),
                "itemID"
            )
            .appendField(
                new Blockly.FieldDropdown(
                    CHECK_HAVE_PLAYER_OPTIONS
                ),
                "playerID"
            );

        this.setInputsInline(true);

        this.setOutput(
            true,
            "boolean"
        );

        this.setColour(20);

        this.setTooltip(
            "Check whether the selected player has the item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_isFacingCorrectItemSide
// ------------------------------------------------------------

Blockly.Blocks["zrms17_isFacingCorrectItemSide"] = {

    init: function () {

        this.appendDummyInput()
            .appendField(
                "is facing correct side of"
            )
            .appendField(
                new Blockly.FieldDropdown(
                    ITEM_OPTIONS
                ),
                "itemID"
            );

        this.setInputsInline(true);

        this.setOutput(
            true,
            "boolean"
        );

        this.setColour(20);

        this.setTooltip(
            "Check whether the robot is facing the correct side of the item."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ============================================================
// OTHER
// ============================================================


// ------------------------------------------------------------
// zrms17_getScore
// ------------------------------------------------------------

Blockly.Blocks["zrms17_getScore"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("get ")
            .appendField(
                new Blockly.FieldDropdown(
                    PLAYER_OPTIONS
                ),
                "playerID"
            )
            .appendField("score");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(20);

        this.setTooltip(
            "Get the selected player's score."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// zrms17_getFuelRemaining
// ------------------------------------------------------------

Blockly.Blocks["zrms17_getFuelRemaining"] = {

    init: function () {

        this.appendDummyInput()
            .appendField("get remaining fuel");

        this.setInputsInline(true);

        this.setOutput(
            true,
            "number"
        );

        this.setColour(20);

        this.setTooltip(
            "Get the remaining fuel."
        );

        this.setHelpUrl("");
    },

    onchange: requireInFunction
};


// ============================================================
// SPHERES PROCEDURE BLOCKS
// ============================================================


// ------------------------------------------------------------
// procedures_definit
// ------------------------------------------------------------

Blockly.Blocks["procedures_definit"] = {

    init: function () {

        this.setHelpUrl(
            Blockly.Msg.PROCEDURES_DEFNORETURN_HELPURL || ""
        );

        this.setColour(290);

        this.appendDummyInput()
            .appendField("global variables");

        this.appendStatementInput("GLOBALS")
            .setCheck("declare");

        this.appendDummyInput()
            .appendField("init");

        this.appendStatementInput("STACK")
            .setCheck("statement");

        this.setTooltip(
            "This code runs once at the start of the match. " +
            "Use it to declare and initialize global variables."
        );

        this.arguments_ = [];

        this.setDeletable(false);
        this.setMovable(false);
    },

    callType_: "procedures_callnoreturn"
};


// ------------------------------------------------------------
// procedures_defloop
// ------------------------------------------------------------

Blockly.Blocks["procedures_defloop"] = {

    init: function () {

        this.setHelpUrl(
            Blockly.Msg.PROCEDURES_DEFNORETURN_HELPURL || ""
        );

        this.setColour(290);

        this.appendDummyInput()
            .appendField("loop");

        this.appendStatementInput("STACK")
            .setCheck("statement");

        this.setTooltip(
            "Main loop. This code runs once per second."
        );

        this.arguments_ = [];

        this.setDeletable(false);
        this.setMovable(false);
    },

    callType_: "procedures_callnoreturn"
};


// ============================================================
// SPHERES BLOCKS
// ============================================================


// ------------------------------------------------------------
// spheres_setPos
// ------------------------------------------------------------

Blockly.Blocks["spheres_setPos"] = {

    init: function () {

        this.setHelpUrl("");

        this.setColour(65);

        this.appendValueInput("X")
            .setCheck("number")
            .appendField("setPos");

        this.appendValueInput("Y")
            .setCheck("number")
            .appendField(",");

        this.appendValueInput("Z")
            .setCheck("number")
            .appendField(",");

        this.setInputsInline(true);

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setTooltip(
            "Set the SPHERES position."
        );
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// spheres_setTarget
// ------------------------------------------------------------

Blockly.Blocks["spheres_setTarget"] = {

    init: function () {

        const FUNCTIONS = [
            [
                "set PositionTarget",
                "api.setPositionTarget"
            ],
            [
                "set AttitudeTarget",
                "api.setAttitudeTarget"
            ],
            [
                "set VelocityTarget",
                "api.setVelocityTarget"
            ],
            [
                "set AttRateTarget",
                "api.setAttRateTarget"
            ],
            [
                "set Forces",
                "api.setForces"
            ],
            [
                "set Torques",
                "api.setTorques"
            ]
        ];

        this.setHelpUrl("");

        this.setColour(65);

        this.appendValueInput("ARGUMENT")
            .appendField(
                new Blockly.FieldDropdown(
                    FUNCTIONS
                ),
                "FUNCTION"
            )
            .setCheck("array");

        this.setInputsInline(true);

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setTooltip(
            "Set a SPHERES target."
        );
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// spheres_getZRState
// ------------------------------------------------------------

Blockly.Blocks["spheres_getZRState"] = {

    init: function () {

        const FUNCTIONS = [
            [
                "get My ZRState",
                "api.getMyZRState"
            ],
            [
                "get Other ZRState",
                "api.getOtherZRState"
            ]
        ];

        this.setHelpUrl("");

        this.setColour(65);

        this.appendValueInput("ARGUMENT")
            .appendField(
                new Blockly.FieldDropdown(
                    FUNCTIONS
                ),
                "FUNCTION"
            )
            .setCheck("array");

        this.setInputsInline(true);

        this.setPreviousStatement(
            true,
            "statement"
        );

        this.setNextStatement(
            true,
            "statement"
        );

        this.setTooltip(
            "Get a SPHERES ZRState."
        );
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// spheres_getTime
// ------------------------------------------------------------

Blockly.Blocks["spheres_getTime"] = {

    init: function () {

        this.setHelpUrl("");

        this.setColour(65);

        this.appendDummyInput()
            .appendField("getTime");

        this.setOutput(
            true,
            "number"
        );

        this.setTooltip(
            "Get the current match time."
        );
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// spheres_I_am_Red
// ------------------------------------------------------------

Blockly.Blocks["spheres_I_am_Red"] = {

    init: function () {

        this.setHelpUrl("");

        this.setColour(65);

        this.appendDummyInput()
            .appendField("I am Red");

        this.setOutput(
            true,
            "number"
        );

        this.setTooltip(
            "Returns whether this SPHERES satellite is red."
        );
    },

    onchange: requireInFunction
};


// ------------------------------------------------------------
// spheres_I_am_Blue
// ------------------------------------------------------------

Blockly.Blocks["spheres_I_am_Blue"] = {

    init: function () {

        this.setHelpUrl("");

        this.setColour(65);

        this.appendDummyInput()
            .appendField("I am Blue");

        this.setOutput(
            true,
            "number"
        );

        this.setTooltip(
            "Returns whether this SPHERES satellite is blue."
        );
    },

    onchange: requireInFunction
};


// ============================================================
// C++ GENERATORS
// ============================================================


// ------------------------------------------------------------
// procedures_definit
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "procedures_definit"
] = function (block, generator) {

    const globals =
        generator.statementToCode(
            block,
            "GLOBALS"
        );

    const statements =
        generator.statementToCode(
            block,
            "STACK"
        );

    return globals + statements;
};


// ------------------------------------------------------------
// procedures_defloop
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "procedures_defloop"
] = function (block, generator) {

    const statements =
        generator.statementToCode(
            block,
            "STACK"
        );

    return statements;
};


// ------------------------------------------------------------
// Generic one-argument function
// ------------------------------------------------------------

function generateOneArgumentNoReturn(
    block,
    generator
) {

    const func =
        block.getFieldValue("FUNCTION");

    const order =
        typeof generator.ORDER_ATOMIC === "number"
            ? generator.ORDER_ATOMIC
            : 0;

    const argument =
        generator.valueToCode(
            block,
            "ARGUMENT",
            order
        ) || "";

    return (
        func +
        "(" +
        argument +
        ");\n"
    );
}


// ------------------------------------------------------------
// spheres_setTarget
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_setTarget"
] = generateOneArgumentNoReturn;


// ------------------------------------------------------------
// spheres_getZRState
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_getZRState"
] = generateOneArgumentNoReturn;


// ------------------------------------------------------------
// spheres_getTime
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_getTime"
] = function (block, generator) {

    const order =
        typeof generator.ORDER_FUNCTION_CALL === "number"
            ? generator.ORDER_FUNCTION_CALL
            : (
                typeof generator.ORDER_ATOMIC === "number"
                    ? generator.ORDER_ATOMIC
                    : 0
            );

    return [
        "api.getTime()",
        order
    ];
};


// ------------------------------------------------------------
// spheres_setPos
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_setPos"
] = function (block, generator) {

    const order =
        typeof generator.ORDER_ATOMIC === "number"
            ? generator.ORDER_ATOMIC
            : 0;

    const x =
        generator.valueToCode(
            block,
            "X",
            order
        ) || "0";

    const y =
        generator.valueToCode(
            block,
            "Y",
            order
        ) || "0";

    const z =
        generator.valueToCode(
            block,
            "Z",
            order
        ) || "0";

    return (
        "setPos(" +
        x +
        ", " +
        y +
        ", " +
        z +
        ");\n"
    );
};


// ------------------------------------------------------------
// spheres_I_am_Blue
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_I_am_Blue"
] = function (block, generator) {

    const order =
        typeof generator.ORDER_ATOMIC === "number"
            ? generator.ORDER_ATOMIC
            : 0;

    return [
        "(api.getSatColor()==SPHERE1)",
        order
    ];
};


// ------------------------------------------------------------
// spheres_I_am_Red
// ------------------------------------------------------------

zrCppGenerator.forBlock[
    "spheres_I_am_Red"
] = function (block, generator) {

    const order =
        typeof generator.ORDER_ATOMIC === "number"
            ? generator.ORDER_ATOMIC
            : 0;

    return [
        "(api.getSatColor()==SPHERE2)",
        order
    ];
};